// Practitioner content library — selectable info blocks per assessment system.
// Keyed by the section `key` used in bodyAssessment.js.
// Jill can populate each system over time; the worksheet shows these as
// clickable chips that append the block into the client's write-up.

export const assessmentLibrary = {
  'nervous-system': [],
  'inflammation': [],
  'gut-barrier': [
    {
      id: 'gut-signs',
      title: 'Signs of Gut Barrier Dysfunction',
      body:
`SIGNS THAT MAY SUGGEST GUT BARRIER DYSFUNCTION

Some are digestive:
• Bloating
• Gas
• Food sensitivities
• Reflux
• Loose stools
• Constipation
• IBS-type symptoms

Some are surprisingly not digestive:
• Brain fog
• Fatigue
• Joint pain
• Skin issues
• Histamine reactions
• Autoimmune conditions
• Mood changes
• Increased anxiety
• Difficulty recovering from illness
• Chronic inflammation`,
    },
    {
      id: 'gut-damage',
      title: 'What Damages the Gut Barrier',
      body:
`WHAT DAMAGES THE GUT BARRIER?

Food Triggers (for susceptible individuals):
• Gluten
• Highly processed foods
• Excess sugar
• Alcohol
• Food sensitivities

Stress — a huge one. Chronic stress can:
• Reduce digestive secretions
• Alter microbiome balance
• Increase cortisol
• Reduce blood flow to the gut
• Increase intestinal permeability
You can eat the perfect diet, but if your nervous system is in survival mode all day, healing is often slower.

Infections & Overgrowths:
• Parasites
• Candida / fungal overgrowth
• Bacterial overgrowth
• Food poisoning history
• Chronic viral burden

Medications, particularly:
• NSAIDs (Advil, Aleve, etc.)
• Long-term antibiotics
• Some acid blockers

Environmental Burden:
• Mold exposure
• Heavy metals
• Certain chemicals and pesticides`,
    },
    {
      id: 'gut-5r',
      title: 'The 5R Approach',
      body:
`THE 5R APPROACH (a common functional medicine framework)

1. REMOVE — reduce what is irritating the gut: trigger foods, alcohol, excess sugar, inflammatory oils, hidden sensitivities. This is where elimination diets and your detox work often fit.

2. REPLACE — support digestion. Some people aren't breaking food down properly. May involve bitters, digestive enzymes, stomach acid support (when appropriate), and thorough chewing. Many people eat good food but don't digest it well.

3. REINOCULATE — support beneficial bacteria: fermented foods, prebiotic foods, probiotics, diverse plant foods. The microbiome helps maintain the gut wall itself.

4. REPAIR — the "healing the barrier" phase:
• L-Glutamine — fuel for the cells lining the gut
• Zinc Carnosine — supports stomach / intestinal lining repair
• Collagen & Bone Broth — amino acids for tissue repair
• Omega-3 Fats — help regulate inflammation
• Aloe Vera — soothing for some (look up Acemannan)
• Deglycyrrhizinated Licorice (DGL) — mucosal support
• Slippery Elm — soothing mucilage layer
• Marshmallow Root — similar protective action
• N-Acetyl Glucosamine — supports mucosal integrity

5. REBALANCE — the piece many programs skip. Healing happens when sleep improves, stress reduces, movement becomes appropriate, circadian rhythm improves, and nervous system regulation improves. The gut and nervous system are dance partners — when one settles, the other often follows.`,
    },
    {
      id: 'gut-foods',
      title: 'Foods That Support Healing',
      body:
`FOODS THAT TEND TO SUPPORT HEALING
(Cooked foods are often easier than raw during healing.)

Proteins: fish, eggs (if tolerated), chicken, turkey, slow-cooked meats
Vegetables: squash, carrots, zucchini, sweet potato, pumpkin, beets
Healthy Fats: olive oil, avocado, coconut, ghee (if tolerated)
Gut-Supportive Foods: bone broth, sauerkraut (if tolerated), water kefir (if tolerated), collagen-rich soups`,
    },
    {
      id: 'gut-often-missed',
      title: 'Things People Often Miss',
      body:
`THINGS PEOPLE OFTEN MISS

The Vagus Nerve — the gut doesn't just need nutrients, it needs safety. Support vagal tone through slow breathing, humming, singing, gargling, meditation, and gentle movement to improve digestion and repair pathways.

Minerals — the gut can't repair efficiently if the body is depleted. Pay attention to magnesium, potassium, sodium, and zinc.

Blood Sugar — repeated glucose spikes can contribute to inflammation. Stable blood sugar often supports healing.`,
    },
    {
      id: 'gut-slow-wellness',
      title: 'A Slow Wellness Perspective',
      body:
`A SLOW WELLNESS PERSPECTIVE

Gut barrier healing is more than a digestive issue. It's usually a conversation between the gut lining, the nervous system, the microbiome, inflammation, energy production (mitochondria), and stress / life circumstances.

Many people spend months trying to "kill things" with antimicrobial protocols, but eventually the question becomes: "What conditions need to exist for the gut to feel safe enough to rebuild?" That's where the deeper healing often begins.

A simple sequence that works surprisingly well:
1. Calm the nervous system
2. Reduce inflammatory foods
3. Improve digestion
4. Address infections / overgrowths if present
5. Rebuild the lining
6. Restore microbial diversity
7. Repeat patiently

Gut tissue can begin turning over within days, but meaningful restoration often takes several months — especially if stress, mold, infections, or chronic inflammation are still active in the background.`,
    },
  ],
  'detox-drainage': [],
  'environmental': [],
  'microbial': [],
  'post-viral': [
    {
      id: 'post-viral-empiric-regimen',
      title: 'Empiric Regimen (Viral Load Support)',
      body:
`EMPIRIC REGIMEN — VIRAL LOAD SUPPORT

The empiric regimen can be continued for 3–12 months or more and be guided by clinical parameters:

• Nattokinase 2000 FU (100 mg) orally twice a day, without food
• Bromelain 500 mg orally once a day, without food
• Curcumin 500 mg orally twice a day (nano, liposomal, or with a piperine additive suggested)`,
    },
  ],
};
